/**
 * Deal email, written in HickoryOS and sent from the viewer's own Gmail (`POST /deals/{id}/emails`).
 *
 * - The Customer's Email button opens a new email at the top of the deal Timeline (`openNew`). Reply on an email
 *   conversation card opens the same Timeline-top composer on Gmail's thread, with its recipient and "Re:" subject
 *   (`openReply`), scrolls there and focuses the body; the card says the reply is open above. A thread Gmail cannot
 *   reply on says so under the card. One composer is open at a time per deal.
 * - Send stages each file straight into a private bucket (progress on its chip), then sends. The send carries one
 *   `client_token` per compose, replayed on Try Again, so the server checks Sent first and nothing sends twice. Staged
 *   files are kept for the retry.
 * - A contact pause holds Send, and the server checks again. Not connected shows how to connect, and Copy Email.
 * - On sent the Timeline row appears at once. Until Gmail's own copy arrives (5–15 minutes) the row reads what was
 *   sent and which files went (`sentEchoFor`); the copy then merges onto the same row.
 * - Every press of the Email button brings the open composer into view (`openCount`), and keeps its draft.
 * - The compose lives here, not in the composer: its words, files, send state and staged uploads survive a reply's
 *   card folding, a filter hiding it, or a send finishing after its card closed.
 * - After a send attempt the email is frozen. Try Again sends exactly that attempt. Only a refusal that never reached
 *   Gmail offers Edit Email, a new send token; any other failure is Try Again, Copy Email or Discard.
 */

import { firstNameOf } from "@/components/records/record-names";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Reply } from "lucide-react";
import { toast } from "sonner";

import {
  type ComposerPhase,
  EMPTY_DRAFT,
  EmailComposer,
  type EmailDraft,
} from "@/components/email-composer/email-composer";
import type { SentEcho } from "@/components/activity-timeline/conversation-card";
import type { ContactHistoryItem } from "@/components/contact-history/types";
import { useOutlookResult } from "@/components/stock-to-proposal/connect-outlook";
import { TestModeLine } from "@/components/test-mode/test-mode-line";
import { Button } from "@/components/ui/button";
import { KeyboardSheet } from "@/components/ui/keyboard-sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/auth-context";
import { useBrands } from "@/hooks/use-brands";
import { useCapabilities } from "@/hooks/use-capabilities";
import { usePersonPauses } from "@/hooks/use-contact-pauses";
import { usePersonalTestMode } from "@/hooks/use-personal-test-mode";
import type { DealDetail } from "@/hooks/use-deal-board";
import {
  type StagedAttachment,
  stageDealEmailAttachment,
  useDealEmailSender,
  useSendDealEmail,
} from "@/hooks/use-deal-email";
import { apiErrorMessage } from "@/lib/api";
import { pauseAsSuppression } from "@/lib/pause-suppression";
import { scrollToStartWithin } from "@/lib/scroll-parent";

interface ComposeState {
  draft: EmailDraft;
  /** One per attempted email, replayed on Try Again. Edit Email mints a new one. */
  token: string;
  phase: ComposerPhase;
  error: string | null;
  progress: Record<string, number>;
  /** The failure is known to have sent nothing: the server refused the email before it reached Gmail (a 4xx). A
   *  `failed` result may come after Gmail was asked to send, and a lost response or a send still running is unknown,
   *  so those offer only Try Again, which checks Sent before anything goes twice. */
  settled: boolean;
  /** Files staged on an earlier try, by composer file id: a retry sends them without uploading again. */
  staged: Record<string, StagedAttachment>;
  /** "me" (default) or "owner" — whose mailbox this compose sends from. */
  sendAs: "me" | "owner";
}

type Compose = ComposeState & ({ id: string; mode: "new" } | { id: string; mode: "reply"; threadKey: string });

function freshCompose(subject = "", sendAs: "me" | "owner" = "me"): ComposeState {
  return {
    draft: { ...EMPTY_DRAFT, subject },
    token: crypto.randomUUID(),
    phase: "editing",
    error: null,
    progress: {},
    settled: false,
    staged: {},
    sendAs,
  };
}

interface DealEmailComposeValue {
  compose: Compose | null;
  /** Opens a new email at the top of the Timeline; a composer already open stays as it is. */
  openNew: () => void;
  /** Counts Email presses, so each one scrolls the new email back into view, even onto a draft already open. */
  openCount: number;
  openReply: (threadKey: string, subject: string, sendAs?: "me" | "owner") => void;
  /** Patches the open compose, if `id` is still the one open (a late send result never lands on another). */
  update: (id: string, patch: Partial<ComposeState> | ((c: Compose) => Partial<ComposeState>)) => void;
  /** Closes the compose `id` (or whatever is open). A send that finishes late never closes a newer compose. */
  close: (id?: string) => void;
  sentEchoFor: (contact: ContactHistoryItem) => SentEcho | null;
  remember: (contactId: string, echo: SentEcho) => void;
  /** The deal is the one on screen. A kept, hidden deal keeps its draft but shows no phone sheet over another deal. */
  onScreen: boolean;
}

const DealEmailComposeContext = createContext<DealEmailComposeValue | null>(null);

/** The deal's composer state, or null where nobody may email on the deal (or outside a deal body). */
// eslint-disable-next-line react-refresh/only-export-components
export function useDealEmailCompose(): DealEmailComposeValue | null {
  return useContext(DealEmailComposeContext);
}

export function DealEmailComposeProvider({
  dealId,
  onScreen = true,
  children,
}: {
  dealId: string | undefined;
  onScreen?: boolean;
  children: ReactNode;
}) {
  // Back from Connect Outlook (started in this composer): say how it went, as the Drips pages do.
  useOutlookResult();
  const { can } = useCapabilities();
  const [compose, setCompose] = useState<Compose | null>(null);
  const [echoes, setEchoes] = useState<Record<string, SentEcho>>({});
  // The body stays mounted when the panel switches deals: a compose belongs to the deal it was opened on.
  useEffect(() => {
    setCompose(null);
    setEchoes({});
  }, [dealId]);

  const [openCount, setOpenCount] = useState(0);
  const openNew = useCallback(() => {
    setCompose((c) => c ?? { ...freshCompose(), id: crypto.randomUUID(), mode: "new" });
    setOpenCount((n) => n + 1);
  }, []);
  const openReply = useCallback(
    (threadKey: string, subject: string, sendAs: "me" | "owner" = "me") => {
      setCompose((c) => c ?? { ...freshCompose(subject, sendAs), id: crypto.randomUUID(), mode: "reply", threadKey });
      // A reply opens in the Timeline-top slot too, so it counts as an open: the slot scrolls to it and focuses it.
      setOpenCount((n) => n + 1);
    },
    [],
  );
  const update = useCallback<DealEmailComposeValue["update"]>(
    (id, patch) =>
      setCompose((c) => (c && c.id === id ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) } : c)),
    [],
  );
  const close = useCallback((id?: string) => setCompose((c) => (!id || c?.id === id ? null : c)), []);
  const value = useMemo<DealEmailComposeValue>(
    () => ({
      compose,
      openNew,
      openCount,
      openReply,
      update,
      close,
      sentEchoFor: (contact) => echoes[contact.id] ?? null,
      remember: (contactId, echo) => setEchoes((prev) => ({ ...prev, [contactId]: echo })),
      onScreen,
    }),
    [compose, openNew, openCount, openReply, update, close, echoes, onScreen],
  );
  if (!dealId || !can("deal_management.email_send")) return <>{children}</>;
  return <DealEmailComposeContext.Provider value={value}>{children}</DealEmailComposeContext.Provider>;
}

/** The error words for a failed send: what to do next, and that nothing was lost. Outlook's codes are the server's
 * own (`outlook.*`); everything else is a Gmail send. */
export function failureWords(error: string | null | undefined): string {
  const code = (error ?? "").toLowerCase();
  if (code === "gmail.unknown")
    return "Gmail didn't answer, so this email may have gone out. Check your Sent folder before you send it again.";
  if (code === "outlook.expired")
    return "The Outlook connection expired. Reconnect Outlook, or copy the email into Outlook.";
  if (code === "outlook.edited")
    return "The draft was changed in Outlook, so it was not sent. Send it from Outlook, or start a new email.";
  if (code.startsWith("outlook."))
    return "Outlook didn't send this one. Your email and files are kept here. Try again, or copy it into Outlook.";
  if (/not_connected|no_grant|grant|auth|scope|insufficient/.test(code))
    return "Gmail did not let Hickory send from your mailbox. Reconnect Gmail, or copy the email into Gmail.";
  if (/recipient|invalid_to|address/.test(code)) return "Gmail rejected the address. Check it and try again.";
  // Gmail thread ids are per mailbox: a thread a teammate's mailbox holds is not in this one.
  if (/thread|not.?found|404/.test(code))
    return "This thread isn't in your Gmail, so you can't reply on it. Discard, then send a new email from the Customer's Email button.";
  return "Gmail didn't respond. Your email and files are kept here. Try again, or copy it into Gmail.";
}

/** The first lines of what was sent, as the conversation card's preview. */
function previewOf(body: string): string {
  return body.replace(/\s+/g, " ").trim().slice(0, 240);
}

/** One composer bound to the deal: its sender, its pauses, and the send. */
function DealComposer({
  layout,
  deal,
  compose,
  threadId,
}: {
  /** `sheet`: the phone's full-screen compose (`EmailComposer`'s compact bar, the body down to the keyboard). */
  layout?: "inline" | "sheet";
  deal: DealDetail;
  compose: Compose;
  threadId: string;
}) {
  const ctx = useDealEmailCompose()!;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: brands } = useBrands();
  const sender = useDealEmailSender(deal.id, true);
  const { data: paused, isSuccess: pausesRead } = usePersonPauses(deal.person_id);
  const send = useSendDealEmail(deal.id);
  // Personal test mode: the server sends this from the viewer's own Gmail to the viewer only.
  const { on: testMode } = usePersonalTestMode();
  const id = compose.id;

  const to = deal.person_email ?? "";
  const pause = (paused?.items ?? []).find((p) => p.status === "active" && p.channels !== "sms");
  const brandName = brands?.find((b) => b.id === deal.tenant_id)?.short_name ?? null;
  const address = sender.data?.mailbox.address ?? user?.email ?? null;
  const from = sender.data ? [user?.display_name, address].filter(Boolean).join(" · ") : null;
  // Whose mailbox this send toasts/opens-in-Gmail as: the owner's address while sending as them.
  const asOwner = compose.sendAs === "owner" && !testMode;
  const sendingMailbox = asOwner ? sender.data?.owner?.mailbox : sender.data?.mailbox;
  const sendingAddress = asOwner
    ? (sendingMailbox?.address ?? sender.data?.owner?.email ?? address)
    : (sendingMailbox?.address ?? address);
  // Outlook mail opens in Outlook, not Gmail: no Gmail link for it.
  const inGmail = sendingMailbox?.provider !== "outlook";

  // Everything below writes through `ctx.update`, so a send that finishes after its card folded still lands.
  const onSend = async (draft: EmailDraft) => {
    const token = compose.token;
    const staged = { ...compose.staged };
    ctx.update(id, { phase: "sending", error: null, settled: false });
    const setProgress = (fileId: string, fraction: number) =>
      ctx.update(id, (c) => ({ progress: { ...c.progress, [fileId]: fraction } }));
    // Until the send request leaves, nothing can have reached Gmail: an upload failure keeps the email editable.
    let requested = false;
    try {
      const attachments: StagedAttachment[] = [];
      for (const f of draft.files) {
        if (!staged[f.id]) {
          staged[f.id] = await stageDealEmailAttachment(deal.id, f.file, (fraction) => setProgress(f.id, fraction));
          ctx.update(id, (c) => ({ staged: { ...c.staged, [f.id]: staged[f.id] } }));
        }
        setProgress(f.id, 1);
        attachments.push(staged[f.id]);
      }
      requested = true;
      const result = await send.mutateAsync({
        to: [to],
        cc: draft.cc,
        subject: draft.subject,
        body: draft.body,
        thread_id: threadId,
        attachments,
        client_token: token,
        send_as: compose.sendAs,
        owner_email: compose.sendAs === "owner" ? sender.data?.owner?.email : undefined,
      });
      if (result.status !== "sent") {
        // "preparing"/"sending": another request still holds this send. Try Again asks the server again, which
        // answers with that attempt rather than sending a second copy.
        ctx.update(id, {
          phase: "failed",
          error:
            result.status === "failed"
              ? failureWords(result.error)
              : "This one is still sending. Try again in a moment to see where it is.",
        });
        return;
      }
      if (result.contact_id) {
        ctx.remember(result.contact_id, { preview: previewOf(draft.body), files: draft.files.map((f) => f.file.name) });
      }
      const files = draft.files.length;
      toast.success(
        `Sent from ${sendingAddress ?? "your mailbox"}${files ? ` with ${files} file${files === 1 ? "" : "s"}` : ""}`,
        {
          action: result.message_id && inGmail
            ? {
                label: "View in Gmail",
                onClick: () =>
                  window.open(
                    `https://mail.google.com/mail/u/${encodeURIComponent(sendingAddress ?? "0")}/#sent/${result.message_id}`,
                    "_blank",
                    "noopener,noreferrer",
                  ),
              }
            : undefined,
        },
      );
      // The timeline's contacts read carries the new row; the deal and board reads its last-contact fields.
      void queryClient.invalidateQueries({ queryKey: ["lead-contacts", deal.id] });
      void queryClient.invalidateQueries({ queryKey: ["dcc", "deal-timeline", deal.id] });
      void queryClient.invalidateQueries({ queryKey: ["dcc", "deal", deal.id] });
      void queryClient.invalidateQueries({ queryKey: ["dcc", "board"] });
      ctx.close(id);
    } catch (err) {
      const { body, status } = err as { body?: { detail?: { error?: string; message?: string } }; status?: number };
      const detail = body?.detail;
      ctx.update(id, {
        phase: "failed",
        // An upload failure or a 4xx refusal sent nothing. A network error, a timeout or a 5xx on the send may have,
        // so only Try Again is offered.
        settled: !requested || (!!status && status >= 400 && status < 500 && status !== 408 && status !== 429),
        error:
          detail && typeof detail === "object" && detail.error === "contact_suppressed"
            ? (detail.message ?? "Not sent: this contact is paused.")
            : apiErrorMessage(err, failureWords(null)),
      });
    } finally {
      ctx.update(id, { progress: {} });
    }
  };

  return (
    <EmailComposer
      layout={layout}
      variant={compose.mode}
      from={from}
      to={testMode ? { name: null, address: user?.email ?? "" } : { name: deal.person_name, address: to }}
      notice={
        <TestModeLine>
          this email goes to you, not {deal.person_name || to}. Its subject starts with [TEST].
        </TestModeLine>
      }
      draft={compose.draft}
      onDraftChange={(draft) => ctx.update(id, { draft })}
      snippets={{
        tenantId: deal.tenant_id,
        brandName,
        fills: {
          first_name: firstNameOf(deal.person_name) ?? undefined,
          rep_name: user?.display_name,
          company: brandName,
          address: deal.location,
        },
      }}
      mailbox={sender.data?.mailbox}
      outlookConnect={!!sender.data?.outlook_connect}
      // Only a ready Gmail grant has its signature read: say nothing rather than "no signature".
      signature={
        sender.data?.mailbox.state === "ready" && sender.data.mailbox.provider === "gmail" ? sender.data : undefined
      }
      owner={testMode ? null : sender.data?.owner}
      sendAs={testMode ? "me" : compose.sendAs}
      onSendAsChange={(sendAs) => ctx.update(id, { sendAs })}
      pause={pause && !testMode ? pauseAsSuppression(pause) : null}
      pauseUnknown={!testMode && !!deal.person_id && !pausesRead}
      phase={compose.phase}
      error={compose.error}
      progress={compose.progress}
      onSend={(draft) => void onSend(draft)}
      onDiscard={() => ctx.close(id)}
      // A new send token: the failed attempt stays as it was, and the edited email is a separate send.
      onEditAsNew={
        compose.settled
          ? () => ctx.update(id, { token: crypto.randomUUID(), phase: "editing", error: null, settled: false })
          : undefined
      }
    />
  );
}

/** The new email, at the top of the Timeline, while one is open. */
export function DealNewEmail({ deal }: { deal: DealDetail }) {
  const ctx = useDealEmailCompose();
  const ref = useRef<HTMLDivElement>(null);
  // The one composer slot, at the top of the Timeline: a new email or a reply (which keeps its thread and subject).
  const compose = ctx?.compose ?? null;
  // Opened from the Customer's Email button, which may sit far above: bring it into view on every press, also
  // when the press finds this draft already open. Never on a keystroke or upload update.
  const openCount = ctx?.openCount ?? 0;
  const open = !!compose;
  // A phone writes the email full screen, the body down to the keyboard, the deal's header out of the way (John Lee,
  // 2026-11-02). A desktop keeps it inline at the top of the Timeline.
  const phone = useIsMobile();
  const [sheetBody, setSheetBody] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open || openCount === 0) return;
    if (phone) {
      // The sheet portals in after this render: focus its body once it is there, which raises the keyboard.
      sheetBody?.querySelector<HTMLTextAreaElement>("textarea")?.focus({ preventScroll: true });
      return;
    }
    // Only the body's own scroll container, never the tray's overflow-hidden shell (see `scrollToStartWithin`).
    scrollToStartWithin(ref.current, { behavior: "smooth" });
    ref.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus({ preventScroll: true });
  }, [open, openCount, phone, sheetBody]);
  if (!compose || !deal.person_email) return null;
  if (phone) {
    // A deal kept mounted behind another keeps its draft, but its sheet never covers the deal on screen.
    if (!ctx?.onScreen) return null;
    return (
      <KeyboardSheet
        open
        // Escape while sending would drop a draft mid-send: blocked, as the Cancel button is.
        onOpenChange={(next) => !next && compose.phase !== "sending" && ctx.close(compose.id)}
        title={`Email ${deal.person_name?.trim() || deal.person_email}`}
        testId="deal-email-sheet"
      >
        <div ref={setSheetBody} className="flex min-h-0 flex-1 flex-col">
          <DealComposer
            key={compose.id}
            layout="sheet"
            deal={deal}
            compose={compose}
            threadId={compose.mode === "reply" ? compose.threadKey : ""}
          />
        </div>
      </KeyboardSheet>
    );
  }
  return (
    <div ref={ref}>
      <DealComposer
        key={compose.id}
        deal={deal}
        compose={compose}
        threadId={compose.mode === "reply" ? compose.threadKey : ""}
      />
    </div>
  );
}

const GMAIL_THREAD_ID = /^[0-9a-f]{10,24}$/i;

/** "Re: …" once, however many times the thread was replied to. */
function replySubject(subject: string | null | undefined): string {
  const s = (subject ?? "").replace(/^Subject:\s*/i, "").trim();
  return /^re:/i.test(s) ? s : `Re: ${s}`;
}

/** Reply, at the foot of an opened email conversation, and the composer in its place once pressed. Only for a
 *  thread Gmail knows (`thread_key`) on a deal whose customer has an email. */
export function DealEmailReply({ deal, contacts }: { deal: DealDetail; contacts: ContactHistoryItem[] }) {
  const ctx = useDealEmailCompose();
  // Cached by useDealEmailSender's queryKey: the composer this opens reads the same query, so this
  // costs no extra fetch. Used only to default the picker; the server re-checks on every send.
  const sender = useDealEmailSender(deal.id, true);
  // Gmail's own thread id (hex). A thread with none carries a Message-ID as its key, which Gmail can't reply on.
  const threadKey = contacts.map((c) => c.thread_key).find((k) => !!k && GMAIL_THREAD_ID.test(k));
  // The thread lives in the owner's mailbox when its NEWEST message was polled from it (not "any
  // message ever" — a stale early hit from before the owner changed must not out-vote where the
  // conversation actually sits now), and the caller may send as them: Reply opens already set to
  // the owner, warning included.
  const owner = sender.data?.owner;
  const threadMessages = contacts.filter((c) => c.thread_key === threadKey);
  const newestInThread = threadMessages.reduce<ContactHistoryItem | null>(
    (latest, c) => (!latest || c.contact_at > latest.contact_at ? c : latest),
    null,
  );
  const threadIsOwners =
    !!owner &&
    owner.mailbox.state === "ready" &&
    newestInThread?.mailbox?.toLowerCase() === owner.email.toLowerCase();
  if (!ctx || !deal.person_email) return null;
  // A thread Gmail cannot reply on says so, rather than Reply silently missing.
  if (!threadKey) {
    return (
      <p className="mt-2 text-[13px] text-muted-foreground" data-testid="reply-unavailable">
        Reply is not available here. This email is not in a Gmail thread HickoryOS can answer. Use Email on the
        customer to write a new one.
      </p>
    );
  }
  const compose = ctx.compose?.mode === "reply" && ctx.compose.threadKey === threadKey ? ctx.compose : null;
  if (compose) {
    return (
      <p className="mt-2 text-[13px] text-muted-foreground" data-testid="reply-open-above">
        Your reply is open at the top of the Timeline.
      </p>
    );
  }
  const busy = ctx.compose !== null;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="mt-2 h-11 gap-1.5 sm:h-8"
      onClick={() =>
        ctx.openReply(threadKey, replySubject(contacts.find((c) => c.subject)?.subject), threadIsOwners ? "owner" : "me")
      }
      disabled={busy}
      title={busy ? "Send or discard the open email first" : undefined}
    >
      <Reply aria-hidden="true" />
      Reply
    </Button>
  );
}
